import React, {useEffect, useState} from "react";
import {useParams} from "react-router-dom";
import {BaseType} from "../../types/Enums";
import ClickableImage from "../../Components/ClickableImage";
import {translate} from "../../types/EnumTranslations";
import {Helmet} from "react-helmet-async";
import {BreadcrumbComponent, BreadcrumbItem} from "../../Components/BreadCrumb";
import InfoPanel, {InfoItem} from "../../Components/InfoPanel";
import {Fancybox} from "@fancyapps/ui";
import {BaseComponentImpl, RoomImpl} from "../../types/implementations";
import {useBuilding} from "../../Helpers/BuildingContext";
import {RoomModel} from "../../types/protocols";
import {isUndefined} from "lodash";

const ComponentPage: React.FC = () => {
    const [component, setComponent] = useState<BaseComponentImpl | null>(null);
    const {buildingKey, itemId } = useParams();
    const { currentBuilding, error, loading, fetchBuilding } = useBuilding();

    useEffect(() => {
        const loadBuildingData = async () => {
            if (buildingKey) {
                await fetchBuilding(buildingKey);
            }
        };

        loadBuildingData().then();
    }, [buildingKey, fetchBuilding]);

    useEffect(()=> {
        if (currentBuilding && itemId) {
            const data = currentBuilding?.model.structure?.components?.[itemId] ?? null;
            if (data) {
                setComponent(new BaseComponentImpl(data))
            }
        }
    }, [currentBuilding])

    if (loading) return <p></p>;
    if (error) return <p style={{color: "#ff0000"}}>{error}</p>;
    if (!component) return <p>No component data available</p>;

    const items = new Array<InfoItem>()
    if (component.specificType) {
        items.push({
            label: "Typ",
            value: translate((component.specificType))
        })
    }
    if (component.label) {
        items.push({
            label: "Eget namn",
            value: component.label
        })
    }

    if (component.cardinalDirection?.value) {
        items.push({
            label: "Väderstreck",
            value: translate(component.cardinalDirection.value)
        })
    }

    if (component.type === BaseType.Roof) {
        items.push({
                label: "Takform",
                value: translate(component.roofShape?.value ?? "")
            },
            {
                label: "Taklutning",
                value: String(component.roofSlope?.value ?? "-")
            }
        )
    }

    if (component.utilityCategory) {
        items.push({
                label: "Kategori",
                value: translate(component.utilityCategory)
            }
        )
        if (component.elevatorType?.value) {
            items.push({
                    label: "Drivsystem",
                    value: translate(component.elevatorType.value)
                }
            )
        }
    }

    if (component.material) {
        items.push({
            label: "Material",
            value: component.material.value?.map((material) => translate(material)).join(", ") || "-"
        });
    }

    if (component.condition) {
        items.push({
            label: "Skick",
            value: component.condition.value ?? 'Ej satt'
        })
    }

    /*if (component.hazardous) {
        const hazards = (component as canBeHazardous).hazardous;
        items.push({
            label: 'Miljö/hälsofara',
            value: hazards ? getHazardsList((component as canBeHazardous)) : "-",
            valueColor: COLORS.deepRed
        })
    }*/

    if (component.buildingRisks?.value) {
        const riskConstruction = component.buildingRisks?.value;
        const risks = new Array<string>()
        riskConstruction.risks?.map((risk)=> {
            return risks.push(translate(risk))
        })
        if (riskConstruction?.customRisk) {
            risks.push(riskConstruction.customRisk)
        }
        items.push({
            label: 'Riskkonstruktion',
            value: risks.length !== 0 ? risks : "-"
        })
    }

    if (component.areaShapes) {
        const area = component.totalArea();
        items.push({
                label: 'Area',
                value: area ? area + " kvm" : "-"
            })
    }

    if (component.depth) {
        const depth = component.depth.value
        items.push({
            label: 'Djup',
            value: depth ? depth + "mm" : "-"
        })
    }

    if (component.insulation) {
        items.push({
            label: 'Isolering',
            value: component.insulation.value ? `${component.insulation.value}  mm` : "-"
        })
    }

    if (component.color) {
        items.push({
            label: 'Färg',
            value: `${translate(component.color?.value?.colorEnum ?? "-")}`
        })
    }

    items.push({
        label: 'Kommentar',
        value: component.comment.value ?? "-"
    })

    const crumbs: BreadcrumbItem[] = [];
    crumbs.push( {
        text: currentBuilding?.summary.name ?? "Byggnad",
        link: `/building/${buildingKey}`
    })

    let roomData: RoomModel | undefined
    roomData = component.roomKey !== undefined
        ? currentBuilding?.model.structure?.rooms?.[component.roomKey]
        : undefined;

    let room: RoomImpl | undefined
    if (roomData) {
        room = new RoomImpl(roomData)
    }

    if (isUndefined(room)) {
        if (component.parentRoomKeys?.value !== undefined) {
            if (Array.isArray(component.parentRoomKeys.value)) {
                for (const roomKey of component.parentRoomKeys.value) {
                    const foundRoom = currentBuilding?.model.structure?.rooms?.[roomKey];
                    if (!isUndefined(foundRoom)) {
                        room = new RoomImpl(foundRoom);
                        break;
                    }
                }
            }
        }
    }

    if (room) {
        if (room.levelKey) {
            const level = currentBuilding?.model.structure?.levels?.[room.levelKey]
            if (level) {
                crumbs.push( {
                    text: `Våning ${level.number}`,
                    link: `/building/${buildingKey}/level/${level.key}`
                })
            }
        }

        crumbs.push( {
            text: `${room.displayName()}`,
            link: `/building/${buildingKey}/room/${room.key}`
        })
    }

    crumbs.push( {
        text: component.displayName()
    })

    return (
        <>
            <BreadcrumbComponent
                items={crumbs}
            />
            <div style={{ padding: "20px", backgroundColor: "#f4f4f4" }}>

                <h3>{component.displayName()}</h3>

                <Helmet>
                    <title>
                        {`${translate(component.type)}: ${translate(component.specificType ?? "")}`}
                    </title>
                </Helmet>

                <div
                    style={{
                        width: "100%",
                        maxWidth: '1100px',
                        display: "flex",
                        flexDirection: "row",
                        borderRadius: "5px",
                        padding: "30px",
                        backgroundColor: "#fff",
                        textAlign: "left",
                        boxShadow: '0 8px 8px rgba(0, 0, 0, 0.03)',
                        gap: '40px',
                    }}
                >

                    <div style={{flex: '1'}}>
                        <h3>Fakta</h3>
                        <InfoPanel items={items} />
                    </div>

                    {component.photos && (
                        <div style={{flex: '1'}}>
                            <h3>Foton</h3>
                            <div style={{display: 'flex', gap: ' 10px', flexWrap: 'wrap'}}>
                                {Object.values(component.photos).map((photo) => {
                                    return  (
                                        <ClickableImage photo={photo} buildingKey={buildingKey!} onImageClick={(photo, url) => {
                                            Fancybox.show(
                                                [
                                                    {
                                                        src: url,
                                                        caption: photo.date,
                                                    }
                                                ],
                                                {
                                                    animated: true,
                                                    showClass: "fancybox-fadeIn",
                                                }
                                            );
                                        }} />
                                    )
                                })}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

export default ComponentPage;
